import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  SelectChangeEvent,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  getWorkers, 
  getVacationRequests, 
  getHolidays, 
  getVacationTypes, 
  getWorkersVacationSummary,
  createVacationRequest,
  updateVacationRequest,
  deleteVacationRequest,
  getWorkSchedules
} from '../services/api';
import { Worker, VacationRequest, Holiday, VacationType, VacationStatus, WorkerVacationSummary, WorkSchedule } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { workCenterAnnualBagsApi } from '../services/workCenterAnnualBagsApi';

type DayStatus = 'holiday' | 'vacation' | 'vacation-previous' | 'work' | 'weekend' | 'pending' | 'partial' | 'partial-previous';

interface DayInfo {
  date: string;
  status: DayStatus;
  description?: string;
  partialHours?: number;
  workHours?: number;
  vacationBagYear?: number;
}

const AnnualView: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string>('');
  const [vacationRequests, setVacationRequests] = useState<VacationRequest[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [vacationTypes, setVacationTypes] = useState<VacationType[]>([]);
  const [workSchedules, setWorkSchedules] = useState<WorkSchedule[]>([]);
  const [vacationSummary, setVacationSummary] = useState<WorkerVacationSummary | null>(null);
  const [previousYearSummary, setPreviousYearSummary] = useState<WorkerVacationSummary | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  
  // Dialog states for vacation creation
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedVacationType, setSelectedVacationType] = useState<VacationType | null>(null);
  const [hours, setHours] = useState<number | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // States for pending vacation approval
  const [pendingVacation, setPendingVacation] = useState<VacationRequest | null>(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);

  // States for vacation deletion
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vacationToDelete, setVacationToDelete] = useState<VacationRequest | null>(null);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Get maximum hours for a specific date based on work center schedule
  const getMaxHoursForDate = (workCenterId: number, date: string): number => {
    const dateObj = new Date(date);
    const dayOfWeek = (dateObj.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0
    
    const schedule = workSchedules.find(ws => 
      ws.work_center_id === workCenterId && 
      ws.day_of_week === dayOfWeek &&
      ws.is_working_day
    );
    
    return schedule ? schedule.work_hours : 8; // Default to 8 if no schedule found
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        workersData, 
        requestsData, 
        holidaysData, 
        vacationTypesData, 
        workSchedulesData,
        summariesData,
        previousYearSummariesData,
        annualBagsData
      ] = await Promise.all([
        getWorkers(),
        getVacationRequests(),
        getHolidays(undefined, selectedYear),
        getVacationTypes(),
        getWorkSchedules(),
        getWorkersVacationSummary(selectedYear),
        getWorkersVacationSummary(selectedYear - 1), // Get previous year data
        workCenterAnnualBagsApi.getAll()
      ]);
      
      setWorkers(workersData);
      setVacationRequests(requestsData);
      setHolidays(holidaysData);
      setVacationTypes(vacationTypesData);
      setWorkSchedules(workSchedulesData);
      
      // Extraer años únicos de las bolsas anuales y ordenarlos
      const years = Array.from(new Set(annualBagsData.map((bag: any) => bag.year))).sort((a: number, b: number) => a - b);
      setAvailableYears(years);
      
      // For any user (admin or not), auto-select their own worker if none selected yet
      if (user && !selectedWorkerId) {
        const currentWorker = workersData.find((w: Worker) => w.id === user.id);
        if (currentWorker) {
          setSelectedWorkerId(currentWorker.id.toString());
          const summary = summariesData.find((s: WorkerVacationSummary) => s.worker_id === currentWorker.id);
          const prevSummary = previousYearSummariesData.find((s: WorkerVacationSummary) => s.worker_id === currentWorker.id);
          setVacationSummary(summary || null);
          setPreviousYearSummary(prevSummary || null);
          return;
        }
      }
      
      // Set vacation summary for selected worker (admin or already selected)
      if (selectedWorkerId) {
        const summary = summariesData.find((s: WorkerVacationSummary) => s.worker_id === parseInt(selectedWorkerId));
        const prevSummary = previousYearSummariesData.find((s: WorkerVacationSummary) => s.worker_id === parseInt(selectedWorkerId));
        setVacationSummary(summary || null);
        setPreviousYearSummary(prevSummary || null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedWorkerId, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleWorkerChange = (event: SelectChangeEvent) => {
    setSelectedWorkerId(event.target.value);
  };

  const handleYearChange = (event: SelectChangeEvent) => {
    const newYear = Number(event.target.value);
    setSelectedYear(newYear);
  };

  // Generate dates for a specific month
  const generateMonthDates = (year: number, month: number) => {
    const dates: string[] = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    for (let day = 1; day <= daysInMonth; day++) {
      // Usar UTC para evitar problemas de zona horaria
      const date = new Date(Date.UTC(year, month, day));
      dates.push(date.toISOString().split('T')[0]);
    }
    
    return dates;
  };

  // Get day status for a worker on a specific date
  const getDayStatus = (worker: Worker, date: string): DayInfo => {
    const dateObj = new Date(date);
    const dayOfWeek = dateObj.getDay();
    
    // Check if it's weekend
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return { date, status: 'weekend', description: 'Fin de semana' };
    }
    
    // Check if it's a holiday for this work center (only from database)
    const holiday = holidays.find(h => 
      h.date === date && 
      h.work_center_id === worker.work_center_id
    );
    if (holiday) {
      return { date, status: 'holiday', description: holiday.name };
    }
    
    // Check if worker has vacation requests for this day
    const approvedVacations = vacationRequests.filter(v => 
      v.worker_id === worker.id && 
      v.status === VacationStatus.APPROVED &&
      new Date(v.start_date) <= dateObj && 
      new Date(v.end_date) >= dateObj
    );
    
    // Check for pending vacations
    const pendingVacations = vacationRequests.filter(v => 
      v.worker_id === worker.id && 
      v.status === VacationStatus.PENDING &&
      new Date(v.start_date) <= dateObj && 
      new Date(v.end_date) >= dateObj
    );
    
    if (pendingVacations.length > 0) {
      return { date, status: 'pending', description: 'Pendiente' };
    }
    
    if (approvedVacations.length > 0) {
      // Check for vacation type 2 (Horas Flexibles) first - always show as partial/green
      const hourlyVacations = approvedVacations.filter(v => v.vacation_type_id === 2);
      
      if (hourlyVacations.length > 0) {
        const totalHours = hourlyVacations.reduce((sum, v) => sum + (v.hours_requested || 0), 0);
        const firstVacation = hourlyVacations[0];
        const isPreviousYearBag = firstVacation.vacation_bag_year && firstVacation.vacation_bag_year < dateObj.getFullYear();
        
        return { 
          date, 
          status: isPreviousYearBag ? 'partial-previous' : 'partial', 
          description: `${totalHours}h`,
          partialHours: totalHours,
          workHours: 8,
          vacationBagYear: firstVacation.vacation_bag_year
        };
      }
      
      // Check for vacation type 1 (Días Completos) - show as blue or purple depending on bag year
      const fullDayVacations = approvedVacations.filter(v => v.vacation_type_id === 1);
      
      if (fullDayVacations.length > 0) {
        const firstVacation = fullDayVacations[0];
        const isPreviousYearBag = firstVacation.vacation_bag_year && firstVacation.vacation_bag_year < dateObj.getFullYear();
        
        return { 
          date, 
          status: isPreviousYearBag ? 'vacation-previous' : 'vacation', 
          description: 'Vacaciones',
          vacationBagYear: firstVacation.vacation_bag_year
        };
      }
    }
    
    return { date, status: 'work', description: 'Trabaja' };
  };

  const getStatusLabel = (dayInfo: DayInfo) => {
    switch (dayInfo.status) {
      case 'holiday': return '';
      case 'vacation': return '';
      case 'vacation-previous': return '';
      case 'partial': return `${dayInfo.partialHours}h`;
      case 'partial-previous': return `${dayInfo.partialHours}h`;
      case 'pending': return '?';
      case 'work': return '✓';
      case 'weekend': return '';
      default: return '';
    }
  };

  // Count vacation days and hours for a month
  const getMonthStats = (worker: Worker, year: number, month: number) => {
    const dates = generateMonthDates(year, month);
    let vacationDays = 0;
    let partialHours = 0;
    
    dates.forEach(date => {
      const dayInfo = getDayStatus(worker, date);
      if (dayInfo.status === 'vacation' || dayInfo.status === 'vacation-previous') {
        vacationDays++;
      } else if ((dayInfo.status === 'partial' || dayInfo.status === 'partial-previous') && dayInfo.partialHours) {
        partialHours += dayInfo.partialHours;
      }
    });
    
    return { vacationDays, partialHours };
  };

  const handleApproveVacation = async () => {
    if (!pendingVacation) return;
    
    try {
      await updateVacationRequest(pendingVacation.id, { status: VacationStatus.APPROVED });
      await fetchData(); // Refresh data
      setApprovalDialogOpen(false);
      setPendingVacation(null);
    } catch (error: any) {
      console.error('Error approving vacation:', error);
      
      // Extract error message from the response
      let errorMessage = 'Error al aprobar la vacación';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const handleRejectVacation = async () => {
    if (!pendingVacation) return;
    
    try {
      await updateVacationRequest(pendingVacation.id, { status: VacationStatus.REJECTED });
      await fetchData(); // Refresh data
      setApprovalDialogOpen(false);
      setPendingVacation(null);
    } catch (error: any) {
      console.error('Error rejecting vacation:', error);
      
      // Extract error message from the response
      let errorMessage = 'Error al rechazar la vacación';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const handleApprovalDialogClose = () => {
    setApprovalDialogOpen(false);
    setPendingVacation(null);
  };

  const handleDeleteVacation = async () => {
    if (!vacationToDelete) return;
    
    try {
      await deleteVacationRequest(vacationToDelete.id);
      await fetchData(); // Refresh data
      setDeleteDialogOpen(false);
      setVacationToDelete(null);
    } catch (error: any) {
      console.error('Error deleting vacation:', error);
      
      // Extract error message from the response
      let errorMessage = 'Error al borrar la vacación';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setVacationToDelete(null);
  };

  // Handle cell click to create vacation
  const handleDayClick = async (worker: Worker, date: string) => {
    const dayInfo = getDayStatus(worker, date);
    
    // If clicking on a pending vacation, handle approval
    if (dayInfo.status === 'pending') {
      const pendingVacation = vacationRequests.find(v => 
        v.worker_id === worker.id && 
        v.status === VacationStatus.PENDING &&
        new Date(v.start_date) <= new Date(date) && 
        new Date(v.end_date) >= new Date(date)
      );
      
      if (pendingVacation) {
        setPendingVacation(pendingVacation);
        setApprovalDialogOpen(true);
        return;
      }
    }
    
    // If clicking on a vacation day (approved), offer to delete it
    if (dayInfo.status === 'vacation' || dayInfo.status === 'vacation-previous' || dayInfo.status === 'partial' || dayInfo.status === 'partial-previous') {
      const vacation = vacationRequests.find(req => 
        req.worker_id === worker.id &&
        req.status === VacationStatus.APPROVED &&
        new Date(req.start_date) <= new Date(date) &&
        new Date(req.end_date) >= new Date(date)
      );
      
      if (vacation) {
        setVacationToDelete(vacation);
        setDeleteDialogOpen(true);
        return;
      }
    }
    
    // Don't allow vacation creation on weekends or holidays
    if (dayInfo.status === 'weekend' || dayInfo.status === 'holiday') {
      return;
    }
    
    // Allow vacation creation on work days
    if (dayInfo.status === 'work') {
      setSelectedWorker(worker);
      setSelectedDate(date);
      setSelectedVacationType(null);
      setHours(null);
      setShowConfirmation(false);
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setShowConfirmation(false);
    setSelectedDate('');
    setSelectedWorker(null);
    setSelectedVacationType(null);
    setHours(null);
  };

  const handleVacationCreate = async () => {
    if (!selectedWorker || !selectedDate || !selectedVacationType) return;
    
    try {
      await createVacationRequest({
        worker_id: selectedWorker.id,
        vacation_type_id: selectedVacationType.id,
        start_date: selectedDate,
        end_date: selectedDate,
        hours_requested: selectedVacationType.id === 2 ? (hours ?? undefined) : undefined,
        notes: '',
        vacation_bag_year: selectedYear
      });
      
      // Refresh data
      await fetchData();
      handleDialogClose();
    } catch (error: any) {
      console.error('Error creating vacation:', error);
      
      // Extract error message from the response
      let errorMessage = 'Error al crear la vacación';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const currentWorker = workers.find(w => w.id === parseInt(selectedWorkerId));

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <Typography 
          sx={{ 
            color: '#666666',
            fontWeight: 300,
            fontSize: '0.9rem'
          }}
        >
          Cargando vista anual...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'start' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 }, mb: { xs: 3, sm: 6 } }}>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 1,
              fontWeight: 700,
              color: '#001923',
              letterSpacing: '-0.01em',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Vista Anual
          </Typography>
          
          <Typography 
            variant="body1"
            className="descripcion-cyan"
            sx={{ 
              fontWeight: 300,
              lineHeight: 1.6
            }}
          >
            Planificación anual por trabajador
          </Typography>
        </Box>

        {/* Controls */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {isAdmin && (
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>Trabajador</InputLabel>
              <Select
                value={selectedWorkerId}
                label="Trabajador"
                onChange={handleWorkerChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    fontWeight: 300
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 300,
                    color: '#666666'
                  }
                }}
              >
                {workers.map(worker => (
                  <MenuItem key={worker.id} value={worker.id} sx={{ fontWeight: 300 }}>
                    {worker.name} {worker.surname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 120 } }}>
            <InputLabel>Año</InputLabel>
            <Select
              value={selectedYear.toString()}
              label="Año"
              onChange={handleYearChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  fontWeight: 300
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 300,
                  color: '#666666'
                }
              }}
            >
              {availableYears.map((year: number) => (
                <MenuItem key={year} value={year} sx={{ fontWeight: 300 }}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {currentWorker && (
        <>
          {/* Worker info and summary */}
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, sm: 4 },
              border: '1px solid #f0f0f0',
              borderRadius: 0,
              mb: { xs: 3, sm: 6 }
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 400, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
              {currentWorker?.name} {currentWorker?.surname}
            </Typography>
            {vacationSummary && (
              <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, flexWrap: 'wrap' }}>
                {/* Always show previous year days */}
                <Box>
                  <Typography variant="body2" sx={{ color: '#666666', textTransform: 'uppercase', fontSize: { xs: '9px', sm: '11px' } }}>
                    Días Disponibles {selectedYear - 1} (Hasta 31/03/{selectedYear})
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 300, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {previousYearSummary 
                      ? `${previousYearSummary.remaining_vacation_days}/${previousYearSummary.annual_vacation_days}`
                      : '0/0'}
                  </Typography>
                </Box>
                
                {/* Always show current year days */}
                <Box>
                  <Typography variant="body2" sx={{ color: '#666666', textTransform: 'uppercase', fontSize: { xs: '9px', sm: '11px' } }}>
                    Días Disponibles {selectedYear}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 300, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {vacationSummary.remaining_vacation_days}/{vacationSummary.annual_vacation_days}
                  </Typography>
                </Box>
                
                {/* Always show flexible hours */}
                <Box>
                  <Typography variant="body2" sx={{ color: '#666666', textTransform: 'uppercase', fontSize: { xs: '9px', sm: '11px' } }}>
                    Horas Disponibles
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 300, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {vacationSummary.remaining_flexible_hours}/{vacationSummary.annual_flexible_hours}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>

          {/* 12 Month Grid */}
          <Grid container spacing={3}>
            {Array.from({ length: 12 }, (_, monthIndex) => {
              const monthStats = getMonthStats(currentWorker, selectedYear, monthIndex);
              const dates = generateMonthDates(selectedYear, monthIndex);
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={monthIndex}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      border: '1px solid #f0f0f0',
                      borderRadius: 0,
                      height: '100%'
                    }}
                  >
                    <CardHeader
                      title={monthNames[monthIndex]}
                      sx={{
                        pb: 1,
                        '& .MuiCardHeader-title': {
                          fontSize: '1.1rem',
                          fontWeight: 400,
                          color: '#1a1a1a'
                        }
                      }}
                    />
                    <CardContent sx={{ pt: 0 }}>
                      {/* Month stats */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontSize: '11px', color: '#666666' }}>
                          {monthStats.vacationDays} días • {monthStats.partialHours}h
                        </Typography>
                      </Box>
                      
                      {/* Mini calendar */}
                      <Box 
                        sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(7, 1fr)', 
                          gap: 0.5,
                          fontSize: '11px'
                        }}
                      >
                        {/* Day headers */}
                        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(day => (
                          <Box 
                            key={day} 
                            sx={{ 
                              textAlign: 'center', 
                              fontWeight: 500, 
                              color: '#666666',
                              p: 0.5
                            }}
                          >
                            {day}
                          </Box>
                        ))}
                        
                        {/* Empty cells for month start */}
                        {Array.from({ length: (() => {
                          const firstDay = new Date(Date.UTC(selectedYear, monthIndex, 1));
                          return (firstDay.getUTCDay() + 6) % 7;
                        })() }, (_, i) => (
                          <Box key={`empty-${i}`} />
                        ))}
                        
                        {/* Days */}
                        {dates.map(date => {
                          const dayInfo = getDayStatus(currentWorker, date);
                          const day = new Date(date).getDate();
                          
                          return (
                            <Box
                              key={date}
                              onClick={() => {
                                if (currentWorker) {
                                  handleDayClick(currentWorker, date);
                                }
                              }}
                              sx={{
                                textAlign: 'center',
                                p: 0.5,
                                minHeight: '20px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 0,
                                fontSize: '10px',
                                backgroundColor: dayInfo.status === 'vacation' ? '#2196F3' : 
                                               dayInfo.status === 'vacation-previous' ? '#9C27B0' :
                                               dayInfo.status === 'partial' ? '#4CAF50' :
                                               dayInfo.status === 'partial-previous' ? '#FF9800' :
                                               dayInfo.status === 'holiday' ? '#f8d7da' :
                                               dayInfo.status === 'pending' ? '#fff3cd' :
                                               dayInfo.status === 'weekend' ? '#9E9E9E' : 'transparent',
                                color: (dayInfo.status === 'vacation' || dayInfo.status === 'vacation-previous' || dayInfo.status === 'partial' || dayInfo.status === 'partial-previous') ? '#ffffff' :
                                       dayInfo.status === 'weekend' ? '#ffffff' : 
                                       dayInfo.status === 'holiday' ? '#721c24' : 
                                       dayInfo.status === 'pending' ? '#856404' : '#1a1a1a',
                                cursor: currentWorker && (dayInfo.status !== 'weekend' && dayInfo.status !== 'holiday') ? 'pointer' : 'default',
                                '&:hover': {
                                  backgroundColor: currentWorker && dayInfo.status === 'work' ? '#e3f2fd' : 
                                                 currentWorker && dayInfo.status === 'pending' ? '#ffeaa7' :
                                                 currentWorker && dayInfo.status === 'vacation' ? '#1976d2' :
                                                 currentWorker && dayInfo.status === 'vacation-previous' ? '#7B1FA2' :
                                                 currentWorker && dayInfo.status === 'partial' ? '#388e3c' :
                                                 currentWorker && dayInfo.status === 'partial-previous' ? '#F57C00' : undefined
                                }
                              }}
                            >
                              <Box component="span">{day}</Box>
                              {dayInfo.status !== 'work' && dayInfo.status !== 'weekend' && (
                                <Box component="span" sx={{ fontSize: '8px', mt: 0.2 }}>
                                  {getStatusLabel(dayInfo)}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Legend */}
          <Box sx={{ mt: { xs: 3, sm: 6 }, p: { xs: 2, sm: 4 }, border: '1px solid #f0f0f0', borderRadius: 0 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 400, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
              Leyenda
            </Typography>
            <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: '#2196F3', border: '1px solid #1976d2', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Días completos (año actual)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: '#9C27B0', border: '1px solid #7B1FA2', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Días completos (año anterior)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: '#4CAF50', border: '1px solid #388e3c', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Horas flexibles (año actual)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: '#FF9800', border: '1px solid #F57C00', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Horas flexibles (año anterior)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: '#fff3cd', border: '1px solid #ffc107', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Pendiente (click para aprobar)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: '#f8d7da', border: '1px solid #dc3545', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Festivos</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Fines de semana</Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {!selectedWorkerId && (
        <Paper 
          elevation={0}
          sx={{ 
            p: 6,
            border: '1px solid #f0f0f0',
            borderRadius: 0,
            textAlign: 'center'
          }}
        >
          <Typography sx={{ color: '#666666', fontWeight: 300 }}>
            {isAdmin 
              ? 'Selecciona un trabajador para ver su planificación anual'
              : 'Cargando tu planificación anual...'}
          </Typography>
        </Paper>
      )}

      {/* Dialog for creating vacation */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {showConfirmation ? 'Confirmar Vacación' : 'Crear Vacación'}
        </DialogTitle>
        <DialogContent>
          {!showConfirmation ? (
            // Creation form
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Trabajador:</strong> {selectedWorker?.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Fecha:</strong> {selectedDate ? new Date(selectedDate).toLocaleDateString('es-ES', { 
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : ''}
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Tipo de Vacación</InputLabel>
                <Select
                  value={selectedVacationType?.id || ''}
                  onChange={(e) => {
                    const type = vacationTypes.find(t => t.id === parseInt(e.target.value as string));
                    setSelectedVacationType(type || null);
                    if (type?.id === 1) setHours(null); // Reset hours for full day
                  }}
                  label="Tipo de Vacación"
                >
                  {vacationTypes.map((type: VacationType) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedVacationType?.id === 2 && (
                <TextField
                  fullWidth
                  type="number"
                  label={`Horas (máximo ${selectedWorker && selectedDate ? getMaxHoursForDate(selectedWorker.work_center_id, selectedDate) : 8})`}
                  value={hours || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    const maxHours = selectedWorker && selectedDate ? getMaxHoursForDate(selectedWorker.work_center_id, selectedDate) : 8;
                    setHours(isNaN(value) ? null : Math.min(maxHours, Math.max(0, value)));
                  }}
                  inputProps={{ 
                    min: 0, 
                    max: selectedWorker && selectedDate ? getMaxHoursForDate(selectedWorker.work_center_id, selectedDate) : 8, 
                    step: 0.5 
                  }}
                  sx={{ mb: 2 }}
                />
              )}
            </Box>
          ) : (
            // Confirmation
            <Typography sx={{ pt: 2 }}>
              ¿Confirmas la creación de esta vacación?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancelar</Button>
          {!showConfirmation ? (
            <Button 
              onClick={() => setShowConfirmation(true)}
              variant="contained"
              disabled={!selectedVacationType || (selectedVacationType?.id === 2 && (!hours || hours <= 0))}
            >
              Continuar
            </Button>
          ) : (
            <Button onClick={handleVacationCreate} variant="contained">
              Confirmar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog for approving pending vacation */}
      <Dialog open={approvalDialogOpen} onClose={handleApprovalDialogClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Aprobar Vacación Pendiente
        </DialogTitle>
        <DialogContent>
          {pendingVacation && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Trabajador:</strong> {workers.find(w => w.id === pendingVacation.worker_id)?.name} {workers.find(w => w.id === pendingVacation.worker_id)?.surname}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Fecha:</strong> {new Date(pendingVacation.start_date).toLocaleDateString('es-ES', { 
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Tipo:</strong> {vacationTypes.find(t => t.id === pendingVacation.vacation_type_id)?.name}
              </Typography>
              {pendingVacation.hours_requested && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Horas:</strong> {pendingVacation.hours_requested}h
                </Typography>
              )}
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Año de Bolsa:</strong> {pendingVacation.vacation_bag_year}
              </Typography>
              
              <Typography variant="body1" sx={{ mt: 3, color: '#666' }}>
                ¿Deseas aprobar o rechazar esta vacación?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleApprovalDialogClose}>Cancelar</Button>
          <Button onClick={handleRejectVacation} color="error">
            Rechazar
          </Button>
          <Button onClick={handleApproveVacation} variant="contained">
            Aprobar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for deleting vacation */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Borrar Vacación
        </DialogTitle>
        <DialogContent>
          {vacationToDelete && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Trabajador:</strong> {workers.find(w => w.id === vacationToDelete.worker_id)?.name} {workers.find(w => w.id === vacationToDelete.worker_id)?.surname}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Fecha:</strong> {new Date(vacationToDelete.start_date).toLocaleDateString('es-ES', { 
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Tipo:</strong> {vacationTypes.find(t => t.id === vacationToDelete.vacation_type_id)?.name}
              </Typography>
              {vacationToDelete.hours_requested && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Horas:</strong> {vacationToDelete.hours_requested}h
                </Typography>
              )}
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Estado:</strong> {vacationToDelete.status === 'APPROVED' ? 'Aprobado' : 
                                        vacationToDelete.status === 'PENDING' ? 'Pendiente' : 
                                        'Rechazado'}
              </Typography>
              
              <Typography variant="body1" sx={{ mt: 3, color: '#d32f2f' }}>
                ¿Estás seguro de que deseas borrar esta vacación?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancelar</Button>
          <Button onClick={handleDeleteVacation} color="error" variant="contained">
            Borrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnnualView;